import { useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

export interface CryptoPickerOption {
  currency: string;
  network: string;
  /** Опциональный «полный» человекочитаемый label (напр. "Bitcoin"). */
  label?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  options: CryptoPickerOption[];
  selected?: CryptoPickerOption | null;
  onSelect: (opt: CryptoPickerOption) => void;
}

// Дефолтные «человеческие» имена — если бэк не пришлёт свой label.
const CURRENCY_NAME: Record<string, string> = {
  BTC: "Bitcoin",
  LTC: "Litecoin",
  TRX: "Tron",
  BNB: "BNB",
  USDT: "Tether",
  USDC: "USD Coin",
  ETH: "Ethereum",
};

const NETWORK_NAME: Record<string, string> = {
  BITCOIN: "Bitcoin network",
  LITECOIN: "Litecoin network",
  TRON: "Tron (TRC20)",
  TRC20: "Tron (TRC20)",
  BEP20: "BNB Smart Chain (BEP20)",
  BSC: "BNB Smart Chain (BEP20)",
  ERC20: "Ethereum (ERC20)",
};

/**
 * Полноэкранный (справа выезжающий) drawer выбора «валюта + сеть»
 * с поиском по тикеру / имени. Стиль — как у `AssetsSearchDrawer`
 * из wallet.
 */
export function CryptoPickerDrawer({
  open,
  onClose,
  title,
  subtitle,
  options,
  selected,
  onSelect,
}: Props) {
  const { t } = useTranslation(["exchange", "common"]);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const name = o.label ?? CURRENCY_NAME[o.currency] ?? "";
      return (
        o.currency.toLowerCase().includes(q) ||
        o.network.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q)
      );
    });
  }, [options, query]);

  const handleSelect = (opt: CryptoPickerOption) => {
    onSelect(opt);
    setQuery("");
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        setQuery("");
        onClose();
      }}
      sx={{ zIndex: 100015 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "100%",
          bgcolor: "background.default",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          // Учитываем системный header Telegram (× Закрыть / статус-бар),
          // иначе заголовок drawer'а заезжает под системную панель.
          padding:
            "calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 20px)",
          boxSizing: "border-box",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "14px",
        }}
      >
        <IconButton
          onClick={() => {
            setQuery("");
            onClose();
          }}
          sx={{ color: "text.primary" }}
          aria-label={t("common:actions.close")}
        >
          <Icon icon="solar:alt-arrow-left-linear" width={22} height={22} />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              sx={{
                fontSize: "12px",
                color: "text.secondary",
                marginTop: "2px",
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {/* Search */}
      <TextField
        autoFocus
        fullWidth
        placeholder={t("exchange:picker_search_placeholder", {
          defaultValue: "Search: USDT, Bitcoin, Tron…",
        })}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon
                icon="solar:magnifer-linear"
                width={20}
                height={20}
                color="currentColor"
              />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setQuery("")}
                sx={{ color: "text.secondary" }}
                aria-label={t("common:actions.clear", { defaultValue: "Clear" })}
              >
                <Icon icon="solar:close-circle-bold" width={18} height={18} />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: {
            borderRadius: "14px",
            bgcolor: "secondary.main",
            "& fieldset": { border: "none" },
            fontSize: "15px",
            paddingRight: query ? "6px" : undefined,
          },
        }}
        sx={{ marginBottom: "14px" }}
      />

      {/* List */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          overflowY: "auto",
          flex: 1,
          paddingBottom: "8px",
          "& > *": { flexShrink: 0 },
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {filtered.length === 0 ? (
          <Box
            sx={{
              padding: "24px 16px",
              borderRadius: "16px",
              bgcolor: "secondary.main",
              textAlign: "center",
              color: "text.secondary",
              fontSize: "14px",
            }}
          >
            {t("common:misc.nothing_found", {
              defaultValue: "Nothing found",
            })}
          </Box>
        ) : (
          filtered.map((opt) => {
            const isSelected =
              selected &&
              selected.currency === opt.currency &&
              selected.network === opt.network;
            const name = opt.label ?? CURRENCY_NAME[opt.currency] ?? opt.currency;
            const netFull = NETWORK_NAME[opt.network] ?? opt.network;
            return (
              <ButtonBase
                key={`${opt.currency}:${opt.network}`}
                onClick={() => handleSelect(opt)}
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: "18px",
                  bgcolor: isSelected
                    ? theme.palette.mode === "dark"
                      ? "rgba(89, 152, 245, 0.14)"
                      : "rgba(89, 152, 245, 0.10)"
                    : theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.04)"
                      : "rgba(0, 0, 0, 0.04)",
                  transition: "background 0.15s",
                  "&:active": {
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.08)",
                  },
                })}
              >
                <CryptoBadgeIconOnly
                  currency={opt.currency}
                  network={opt.network}
                />

                <Box sx={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "text.primary",
                      lineHeight: 1.2,
                    }}
                  >
                    {name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "text.secondary",
                      marginTop: "2px",
                    }}
                  >
                    {netFull}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "2px",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "text.primary",
                      lineHeight: 1,
                    }}
                  >
                    {opt.currency}
                  </Typography>
                  {isSelected ? (
                    <Icon
                      icon="solar:check-circle-bold"
                      width={18}
                      height={18}
                      color="var(--tg-color-accent, #5998F5)"
                    />
                  ) : null}
                </Box>
              </ButtonBase>
            );
          })
        )}
      </Box>
    </Drawer>
  );
}

// ================================================================
// Локальный «только иконка» вариант бейджа — в списке нам не нужен
// текст справа от кружка (мы отдельно рендерим name/network label).
// ================================================================

const CURRENCY_ICON: Record<string, string> = {
  BTC: "/crypto-icons/btc.png",
  LTC: "/crypto-icons/ltc.png",
  TRX: "/crypto-icons/trx.png",
  BNB: "/crypto-icons/bnb.png",
  USDT: "/crypto-icons/usdt.png",
  USDC: "/crypto-icons/usdc.png",
  ETH: "/crypto-icons/eth.png",
};
const NETWORK_VARIANT: Record<string, string> = {
  "USDT:TRC20": "/crypto-icons/usdt-trc20.png",
  "USDT:BEP20": "/crypto-icons/usdt-bep20.png",
  "USDC:TRC20": "/crypto-icons/usdc-trc20.png",
  "USDC:BEP20": "/crypto-icons/usdc-bep20.png",
};

function CryptoBadgeIconOnly({
  currency,
  network,
}: {
  currency: string;
  network: string;
}) {
  const src =
    NETWORK_VARIANT[`${currency}:${network}`] ??
    CURRENCY_ICON[currency] ??
    "";
  return (
    <Box
      sx={(theme) => ({
        width: 42,
        height: 42,
        borderRadius: "50%",
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.05)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        flexShrink: 0,
      })}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={currency}
          sx={{ width: 42, height: 42, objectFit: "cover" }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      ) : (
        <Typography
          sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}
        >
          {currency.slice(0, 3)}
        </Typography>
      )}
    </Box>
  );
}
